import { resolveTaskTreePath } from '../../utils/task-tree-path.js';

export type RequestedTaskRefResolution =
  | { status: 'ok'; refs: string[] }
  | { status: 'ambiguous'; ref: string; candidates: string[] };

/**
 * WHICH TASK A TYPED REF MEANS, decided once at a tool boundary instead of by each pipeline that consumes it.
 *
 * Everything downstream compares refs in their normalised form, so a ref has to already name ONE node by the
 * time it gets there. The pipelines used to paper over a bare label ("a") by also matching a node's own
 * `taskNumber` at any depth, which silently picked whichever branch a tree walk happened to reach first — the
 * re-grade dispatch did this and then failed to match at grading time, and the MS/GS refine walkers still do.
 * Resolving here turns "a" into "1.a" when that is unambiguous and into a question when it is not.
 *
 * A ref that names a MAIN TASK wins before the tree search runs: an undotted ref matches a node's own label at
 * any depth, and a nested label may legitimately repeat a main task's (a sub-question that restarts at "1"
 * inside a task frame), which would otherwise turn "Aufgabe 2" into an ambiguity question.
 *
 * A ref that resolves to nothing is returned unchanged — it matches no task downstream, and each caller
 * already has a "nothing matched" path that names the exam's real refs back to the agent.
 */
export function resolveRequestedTaskRefs(examTasks: unknown, requestedRefs: string[]): RequestedTaskRefResolution {
  const tasks = Array.isArray(examTasks) ? examTasks : [];
  const topLevelTaskNumbers = new Set(
    tasks.map((task: any) => String(task?.taskNumber ?? '').trim()).filter(Boolean),
  );

  const refs: string[] = [];
  for (const rawRef of requestedRefs) {
    const ref = String(rawRef ?? '').trim();
    if (!ref) continue;
    if (topLevelTaskNumbers.has(ref)) {
      refs.push(ref);
      continue;
    }
    const resolved = resolveTaskTreePath(tasks, ref, 'subTasks');
    if (resolved.status === 'ambiguous') {
      return { status: 'ambiguous', ref, candidates: resolved.taskNumberPaths.map(path => path.join('.')) };
    }
    refs.push(resolved.status === 'found' ? resolved.taskNumberPath.join('.') : ref);
  }
  return { status: 'ok', refs };
}

/** Every ref this exam can be addressed by, as the dotted paths the tools expect ("1", "1.1a", "1.1a.i"). */
export function addressableTaskRefs(tasks: unknown, prefix: string[] = [], out: string[] = []): string[] {
  for (const task of Array.isArray(tasks) ? tasks : []) {
    const taskNumber = String((task as any)?.taskNumber ?? '').trim();
    if (!taskNumber) continue;
    const path = [...prefix, taskNumber];
    out.push(path.join('.'));
    addressableTaskRefs((task as any)?.subTasks, path, out);
  }
  return out;
}

/** The one wording for "that ref names more than one task", so all three tools ask the same question. */
export function ambiguousTaskRefMessage(ref: string, candidates: string[], actionDescription: string): string {
  return (
    `SYSTEM INFO: Abbruch. Es wurde NICHTS geändert. "${ref}" ist in dieser Klausur nicht eindeutig — ` +
    `es passt auf: ${candidates.map(candidate => `"${candidate}"`).join(', ')}. Melde dem Nutzer NICHT, dass ` +
    `${actionDescription} wurde. Frage beim Nutzer nach, welche dieser Aufgaben gemeint ist, und rufe das Tool ` +
    'dann mit der vollständigen Aufgabennummer erneut auf.'
  );
}
